import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { AlertTriangle, CheckCircle2, Calculator, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { PageHeader, Field, Money, Spinner } from "@/components/common";
import Typeahead from "@/components/Typeahead";
import AttachmentUploader from "@/components/AttachmentUploader";
import MasterDialog from "@/components/MasterDialog";
import BillDrawer from "@/components/BillDrawer";
import { api, formatErr } from "@/lib/api";
import { fmtDate, fmtDateTime, fmtMoney, riyadhToday } from "@/lib/format";
import DateInput from "@/components/DateInput";
import { useAuth } from "@/context/AuthContext";

import { CompanyStage, SessionBanner, focusTid } from "@/components/ClientSession";
import PendingUploads, { UploadImages, ExtractedPanel } from "@/components/PendingUploads";
import { X as XIcon } from "lucide-react";

const focusMode = (on) => window.dispatchEvent(new CustomEvent("ksavat-focus", { detail: on }));

const EMPTY = { client_id: "", invoice_date: riyadhToday(), invoice_number: "", vendor: null, vendor_vat_number: "", item: null, before_vat: "", vat_paid: "", sponsor_name: "" };
const INV_RE = /^[A-Za-z0-9\-/_.#&()+:\s]*$/;

const DuplicateAlert = ({ dups }) => (
  <div className="border border-amber-300 bg-amber-50 rounded-md p-3 text-sm" data-testid="duplicate-invoice-warning-alert">
    <div className="flex items-center gap-2 font-medium text-amber-800"><AlertTriangle className="w-4 h-4" />Potential duplicate — same client, vendor and invoice number</div>
    <ul className="mt-1.5 text-xs text-amber-900 space-y-0.5 num">
      {dups.map((d) => <li key={d.id}>{d.record_no} · {fmtDate(d.invoice_date)} · {fmtMoney(d.total)} SAR · {d.created_by_name} · {fmtDateTime(d.created_at)}</li>)}
    </ul>
  </div>
);

export default function BillEntry() {
  const { id } = useParams();
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const { settings } = useAuth();
  const [clients, setClients] = useState([]);
  const [f, setF] = useState({ ...EMPTY, client_id: sp.get("client") || "" });
  const [files, setFiles] = useState([]);
  const [reason, setReason] = useState("");
  const [dups, setDups] = useState([]);
  const [confirmDup, setConfirmDup] = useState(false);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(!!id);
  const [adding, setAdding] = useState(null);
  const [saved, setSaved] = useState(null);
  const [upload, setUpload] = useState(null);
  const [queue, setQueue] = useState([]);
  const [tab, setTab] = useState("entry");
  const [pendingCount, setPendingCount] = useState(0);
  useEffect(() => () => focusMode(false), []);
  useEffect(() => {
    if (!f.client_id || id) return;
    api.get("/client-pending-counts", { params: { client_id: f.client_id } }).then(({ data }) => setPendingCount(data.uploads)).catch(() => {});
  }, [f.client_id, id]);
  const prefill = async (u) => {
    let up = u;
    try { const { data } = await api.get("/client-uploads", { params: { client_id: u.client_id, status: "pending" } }); up = data.find((d) => d.id === u.id) || u; setUpload(up); } catch { /* keep */ }
    const x = up.extracted || {};
    setF((p) => ({ ...p, invoice_date: x.invoice_date || up.bill_date, invoice_number: x.invoice_number || "", vendor: null, vendor_vat_number: x.vendor_vat_number || "", item: null,
      before_vat: x.before_vat != null ? String(x.before_vat) : "", vat_paid: x.vat_amount != null ? String(x.vat_amount) : "" }));
    if (x.vendor_vat_number || x.vendor_name) {
      const { data } = await api.get("/vendors", { params: { q: x.vendor_vat_number || x.vendor_name, limit: 10 } });
      const v = data.find((r) => (x.vendor_vat_number ? r.vat_number === x.vendor_vat_number : r.name.toLowerCase() === x.vendor_name.toLowerCase()));
      if (v) { setF((p) => ({ ...p, vendor: v, vendor_vat_number: v.vat_number || p.vendor_vat_number })); }
      else if (x.vendor_name) { setAdding({ kind: "vendors", name: x.vendor_name, values: { vat_number: x.vendor_vat_number || "" } }); }
    }
    if (x.item_category) {
      const { data } = await api.get("/items", { params: { q: x.item_category, limit: 10 } });
      const c = x.item_category.toLowerCase();
      const it = data.find((r) => r.name.toLowerCase() === c) || data.find((r) => (r.category || "").toLowerCase() === c);
      if (it) { set("item", it); }
    }
  };
  const refreshUpload = async () => {
    const { data } = await api.get("/client-uploads", { params: { client_id: upload.client_id, status: "pending" } });
    const up = data.find((d) => d.id === upload.id);
    if (!up) { return; }
    setUpload(up);
    const x = up.extracted || {};
    setF((p) => ({ ...p, invoice_number: p.invoice_number || x.invoice_number || "", vendor_vat_number: p.vendor_vat_number || x.vendor_vat_number || "",
      before_vat: p.before_vat !== "" ? p.before_vat : x.before_vat != null ? String(x.before_vat) : "", vat_paid: p.vat_paid !== "" ? p.vat_paid : x.vat_amount != null ? String(x.vat_amount) : "" }));
    toast.success("Bill details refreshed — only empty fields were filled");
  };
  const startUpload = (u, rows) => {
    setQueue(rows); setUpload(u); setTab("entry"); focusMode(true); prefill(u);
    setTimeout(() => focusTid("bill-invoice-number-input"), 120);
  };
  const exitUpload = () => { setUpload(null); focusMode(false); setTab("pending"); };
  const goNext = (done) => {
    const idx = queue.findIndex((q) => q.id === upload.id);
    const rest = done ? queue.filter((q) => q.id !== upload.id) : queue;
    if (done) setPendingCount(rest.length);
    if (!rest.length) { exitUpload(); toast.success("All pending bills processed"); return; }
    const nxt = done ? rest[Math.min(idx, rest.length - 1)] : queue[(idx + 1) % queue.length];
    setQueue(rest); setUpload(nxt); prefill(nxt);
  };
  const [drawer, setDrawer] = useState(false);
  const rate = Number(settings?.standard_vat_rate ?? 15);
  const wrapRef = useRef(null);
  const [picked, setPicked] = useState(null);
  const client = clients.find((c) => c.id === f.client_id) || (picked?.id === f.client_id ? picked : null);

  useEffect(() => {
    api.get("/clients", { params: { status: "active" } }).then(({ data }) => {
      setClients(data);
      const pre = data.find((c) => c.id === sp.get("client"));
      if (pre && !id) pickClient(pre);
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!id) return;
    api.get(`/bills/${id}`).then(({ data: b }) => {
      setF({ client_id: b.client_id, invoice_date: b.invoice_date, invoice_number: b.invoice_number, vendor: { id: b.vendor_id, name: b.vendor_name },
        vendor_vat_number: b.vendor_vat_number, item: { id: b.item_id, name: b.item_name }, before_vat: String(b.before_vat), vat_paid: String(b.vat_paid), sponsor_name: b.sponsor_name || "" });
      setLoading(false);
    }).catch((e) => { toast.error(formatErr(e)); nav("/bills"); });
  }, [id, nav]);

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const total = (Number(f.before_vat) || 0) + (Number(f.vat_paid) || 0);
  const setBefore = (v) => setF((p) => ({ ...p, before_vat: v, vat_paid: v === "" ? "" : (Math.round(Number(v) * rate) / 100).toFixed(2) }));

  useEffect(() => {
    if (!f.client_id || !f.vendor || !f.invoice_number.trim()) { setDups([]); return undefined; }
    const t = setTimeout(() => {
      api.get("/bills/check-duplicate", { params: { client_id: f.client_id, vendor_id: f.vendor.id, invoice_number: f.invoice_number, exclude_id: id } })
        .then(({ data }) => setDups(data)).catch(() => setDups([]));
    }, 400);
    return () => clearTimeout(t);
  }, [f.client_id, f.vendor, f.invoice_number, id]);

  const errors = useMemo(() => {
    const e = {};
    if (f.invoice_number && !INV_RE.test(f.invoice_number)) e.invoice_number = "Only letters, digits and - / _ . # & ( ) + : allowed";
    if (f.invoice_date > riyadhToday()) e.invoice_date = "Invoice date cannot be in the future";
    if (settings?.enforce_vat_format && f.vendor_vat_number && !/^3\d{13}3$/.test(f.vendor_vat_number)) e.vendor_vat_number = "Must be 15 digits starting and ending with 3";
    if (f.before_vat !== "" && Number(f.before_vat) < 0) e.before_vat = "Cannot be negative";
    if (f.vat_paid !== "" && Number(f.vat_paid) < 0) e.vat_paid = "Cannot be negative";
    return e;
  }, [f, settings]);

  const needFiles = !id && !upload && settings?.require_attachment;
  const ready = f.client_id && f.invoice_date && f.invoice_number.trim() && f.vendor && f.item && f.before_vat !== "" && f.vat_paid !== ""
    && !Object.keys(errors).length && (!needFiles || files.length) && (!id || reason.trim().length >= 3);

  function pickClient(c) {
    if (!c) return;
    setPicked(c);
    setF((p) => ({ ...p, client_id: c.id, sponsor_name: id ? p.sponsor_name : c.sponsor_name || p.sponsor_name }));
    setTimeout(() => focusTid("bill-invoice-date-input"), 80);
  }

  const submit = async (confirm = false) => {
    setBusy(true);
    const body = { client_id: f.client_id, invoice_date: f.invoice_date, invoice_number: f.invoice_number.trim(), vendor_id: f.vendor.id,
      vendor_vat_number: f.vendor_vat_number.trim(), item_id: f.item.id, before_vat: Number(f.before_vat), vat_paid: Number(f.vat_paid),
      sponsor_name: f.sponsor_name || "", attachment_ids: files.map((x) => x.id), client_upload_id: upload?.id || null, confirm_duplicate: confirm };
    try {
      if (id) {
        await api.put(`/bills/${id}`, { ...body, reason });
        toast.success("Correction saved with revision history");
        nav("/bills");
      } else {
        const { data } = await api.post("/bills", body);
        setSaved(data);
        toast.success(`Bill ${data.record_no} saved`);
        setF((p) => ({ ...EMPTY, client_id: p.client_id, sponsor_name: p.sponsor_name, invoice_date: p.invoice_date }));
        setFiles([]);
        setDups([]);
        if (upload) goNext(true);
        setTimeout(() => focusTid("bill-invoice-number-input"), 80);
      }
      setConfirmDup(false);
    } catch (e) {
      if (e.response?.status === 409 && e.response.data.detail?.duplicates && !e.response.data.detail.blocked) setConfirmDup(true);
      else toast.error(formatErr(e));
    } finally {
      setBusy(false);
    }
  };

  const onKeys = (e) => {
    if (e.key !== "Enter") return;
    if (e.ctrlKey || e.metaKey) { e.preventDefault(); if (ready && !busy) submit(false); return; }
    const t = e.target;
    if (t.tagName === "BUTTON" || t.tagName === "TEXTAREA" || !t.hasAttribute("data-nav")) return;
    e.preventDefault();
    const els = [...wrapRef.current.querySelectorAll("[data-nav]")].filter((el) => !el.disabled);
    els[els.indexOf(t) + 1]?.focus();
  };

  if (loading || (id && !client && !clients.length)) return <Spinner />;

  return (
    <div>
      <PageHeader eyebrow={id ? "Correction" : "Data entry"} title={id ? "Correct purchase bill" : "Purchase bill entry"}
        subtitle={id ? "All changes are recorded with previous values, your reason, timestamp and IP." : "Record a purchase invoice for an assigned client. Timestamp and IP are captured automatically on the server (Asia/Riyadh)."}
        actions={id && <Button variant="outline" onClick={() => nav(-1)}><ArrowLeft className="w-4 h-4 mr-1.5" />Back</Button>} />

      {saved && (
        <div className="mb-5 panel border-emerald-300 bg-emerald-50 px-4 py-3 flex flex-wrap items-center gap-3 fade-up" data-testid="bill-saved-confirmation">
          <CheckCircle2 className="w-5 h-5 text-emerald-700" />
          <div className="text-sm text-emerald-900 flex-1"><span className="num font-semibold">{saved.record_no}</span> saved · INV {saved.invoice_number} · {saved.vendor_name} · <span className="num">{fmtMoney(saved.total)} SAR</span></div>
          <Button size="sm" variant="outline" onClick={() => setDrawer(true)} data-testid="view-saved-bill-button">View record</Button>
        </div>
      )}

      {!client ? <CompanyStage onPick={pickClient} /> : (<>
      <SessionBanner client={client} onChange={() => { setF((p) => ({ ...p, client_id: "" })); setDups([]); }} />
      {!id && !upload && (
        <div className="flex gap-2 mb-4" data-testid="entry-tabs">
          {[["entry", "New entry"], ["pending", "Pending bills"]].map(([k, l]) => (
            <button key={k} type="button" onClick={() => setTab(k)} data-testid={`entry-tab-${k}`}
              className={`px-4 py-2 rounded-md text-sm font-medium border transition-colors ${tab === k ? "bg-emerald-800 text-white border-emerald-800" : "bg-white text-slate-600 border-slate-200 hover:border-emerald-400"}`}>
              {l}{k === "pending" && <span className={`ml-2 num text-[11px] rounded-full px-2 py-0.5 ${tab === k ? "bg-white/20" : "bg-amber-100 text-amber-800"}`} data-testid="pending-count-badge">{pendingCount}</span>}
            </button>
          ))}
        </div>
      )}
      {tab === "pending" && !upload ? <PendingUploads clientId={f.client_id} onEnter={startUpload} onCount={setPendingCount} /> : (
      <div className={upload ? "grid xl:grid-cols-2 gap-6 items-start" : ""}>
      <div ref={wrapRef} onKeyDown={onKeys} className={`grid ${upload ? "" : "xl:grid-cols-[1fr_380px]"} gap-6`}>
        <div className="panel p-5 sm:p-6 space-y-6 fade-up" data-testid="bill-entry-form">
          <section>
            <div className="eyebrow mb-3">1 · Invoice</div>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Invoice date" required error={errors.invoice_date} hint="dd/mm/yyyy · Riyadh calendar">
                <DateInput max={riyadhToday()} value={f.invoice_date} onChange={(v) => set("invoice_date", v)} className="h-10" data-nav testid="bill-invoice-date-input" />
              </Field>
              <Field label="Invoice number" required error={errors.invoice_number} hint={`${f.invoice_number.length}/25 · letters, digits & symbols`}>
                <Input maxLength={25} value={f.invoice_number} onChange={(e) => set("invoice_number", e.target.value)} className="h-10 num" placeholder="e.g. INV-2026/0045" data-nav data-testid="bill-invoice-number-input" />
              </Field>
            </div>
          </section>

          <section>
            <div className="eyebrow mb-3">2 · Vendor & item</div>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Vendor name" required>
                <Typeahead endpoint="/vendors" value={f.vendor} placeholder="Search vendor name or VAT no." nav testid="vendor-typeahead-input"
                  meta={(r) => r.vat_number || r.code} onAddNew={(t) => setAdding({ kind: "vendors", name: t })}
                  onSelect={(v) => setF((p) => ({ ...p, vendor: v, vendor_vat_number: v ? v.vat_number || "" : p.vendor_vat_number }))} />
              </Field>
              <Field label="Vendor VAT number" error={errors.vendor_vat_number} hint="Confirm against the invoice">
                <Input value={f.vendor_vat_number} maxLength={15} onChange={(e) => set("vendor_vat_number", e.target.value.replace(/\D/g, ""))} className="h-10 num" placeholder="3XXXXXXXXXXXXX3" data-nav data-testid="vendor-vat-no-input" />
              </Field>
              <Field label="Item name" required>
                <Typeahead endpoint="/items" value={f.item} placeholder="Search item" nav testid="item-typeahead-input"
                  meta={(r) => r.category || r.code} onAddNew={(t) => setAdding({ kind: "items", name: t })} onSelect={(v) => set("item", v)} />
                {upload?.extracted?.item_category && !f.item && (
                  <button type="button" onClick={() => setAdding({ kind: "items", name: upload.extracted.item_category, values: { category: upload.extracted.item_category } })}
                    className="mt-1 text-xs text-violet-700 hover:underline" data-testid="item-suggestion-add">AI suggests “{upload.extracted.item_category}” — add as new item</button>
                )}
              </Field>
              <Field label="Sponsor / Customer name (optional)" hint="Defaults from client profile">
                <Input value={f.sponsor_name} onChange={(e) => set("sponsor_name", e.target.value)} className="h-10" data-nav data-testid="bill-sponsor-input" />
              </Field>
            </div>
            {dups.length > 0 && <div className="mt-4"><DuplicateAlert dups={dups} /></div>}
          </section>

          <section>
            <div className="eyebrow mb-3">3 · Amounts (SAR)</div>
            <div className="grid sm:grid-cols-3 gap-4">
              <Field label="Before VAT price" required error={errors.before_vat}>
                <Input type="number" min="0" step="0.01" value={f.before_vat} onChange={(e) => setBefore(e.target.value)} className="h-10 num text-right" placeholder="0.00" data-nav data-testid="before-vat-amount-input" />
              </Field>
              <Field label={`VAT amount (${rate}% auto)`} required error={errors.vat_paid}>
                <Input type="number" min="0" step="0.01" value={f.vat_paid} onChange={(e) => set("vat_paid", e.target.value)} className="h-10 num text-right" placeholder="0.00" data-nav data-testid="vat-paid-amount-input" />
              </Field>
              <Field label="Total (auto)">
                <div className="h-10 px-3 flex items-center justify-end rounded-md bg-emerald-50 border border-emerald-200 num font-semibold text-emerald-900" data-testid="total-amount-display">{fmtMoney(total)}</div>
              </Field>
            </div>
            {f.before_vat !== "" && (
              <div className="mt-3 flex items-center gap-2 text-xs text-slate-500" data-testid="vat-rate-hint">
                <Calculator className="w-3.5 h-3.5" />VAT auto-calculated at {rate}% (set in System Settings). Adjust only if the invoice shows a different amount.
              </div>
            )}
          </section>

          <section>
            <div className="eyebrow mb-3">4 · Source document {needFiles && <span className="text-red-500">*</span>}</div>
            {id ? <p className="text-sm text-slate-500">Manage attachments from the record drawer (Attachments tab).</p> : upload ? <p className="text-sm text-emerald-700" data-testid="upload-auto-attach-note">{upload.images.length} client image(s) from {upload.upload_no} will be attached automatically.</p> : <AttachmentUploader files={files} onChange={setFiles} />}
          </section>

          {id && (
            <section>
              <Field label="Reason for correction" required>
                <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} placeholder="e.g. VAT amount mistyped; corrected per original invoice" data-nav data-testid="bill-correction-reason-input" />
              </Field>
            </section>
          )}
        </div>

        <aside className="space-y-4">
          <div className="panel p-5 xl:sticky xl:top-20 fade-up" style={{ animationDelay: "80ms" }}>
            <div className="eyebrow mb-3">Entry summary</div>
            <dl className="space-y-2 text-sm">
              {[["Client", client?.company_name], ["Invoice", f.invoice_number], ["Date", fmtDate(f.invoice_date)], ["Vendor", f.vendor?.name], ["Item", f.item?.name], ["Attachments", id ? "—" : upload ? `${upload.images.length} client image(s)` : `${files.length} file(s)`]].map(([k, v]) => (
                <div key={k} className="flex justify-between gap-3"><dt className="text-slate-500">{k}</dt><dd className="text-slate-800 text-right truncate">{v || "—"}</dd></div>
              ))}
            </dl>
            <div className="border-t border-slate-200 mt-4 pt-4 space-y-1.5">
              <div className="flex justify-between text-sm"><span className="text-slate-500">Before VAT</span><Money value={f.before_vat} /></div>
              <div className="flex justify-between text-sm"><span className="text-slate-500">VAT</span><Money value={f.vat_paid} /></div>
              <div className="flex justify-between text-base pt-1"><span className="font-medium">Total</span><Money value={total} strong /></div>
            </div>
            <Button className="w-full mt-5 h-11 bg-emerald-800 hover:bg-emerald-900" disabled={!ready || busy} onClick={() => submit(false)} data-nav data-testid="bill-entry-form-submit-button">
              {busy ? "Saving…" : id ? "Save correction" : "Save purchase bill"}
            </Button>
            {!ready && <p className="text-[11px] text-slate-400 mt-2 text-center">Complete all required fields{needFiles ? " and attach the source document" : ""}.</p>}
          </div>
        </aside>
      </div>
      {upload && (
        <div className="xl:sticky xl:top-20 panel p-3 space-y-2" data-testid="entry-image-panel">
          <div className="flex items-center justify-between gap-2 text-sm">
            <div><span className="num font-medium">{upload.upload_no}</span> · Bill date <span className="num">{fmtDate(upload.bill_date)}</span> <span className="text-xs text-slate-400" data-testid="entry-queue-count">· {queue.length} pending</span></div>
            <div className="flex gap-2">
              <Button size="sm" className="bg-emerald-800 hover:bg-emerald-900" disabled={!ready || busy} onClick={() => submit(false)} data-testid="entry-save-next-button">{busy ? "Saving…" : "Save & next"}</Button>
              <Button size="sm" variant="outline" disabled={queue.length < 2} onClick={() => goNext(false)} data-testid="entry-skip-button">Skip</Button>
              <Button size="sm" variant="ghost" onClick={exitUpload} data-testid="entry-exit-button"><XIcon className="w-4 h-4 mr-1" />Close</Button>
            </div>
          </div>
          <ExtractedPanel upload={upload} compact onRefresh={refreshUpload} />
          <UploadImages upload={upload} className="h-[calc(100vh-290px)] min-h-[420px]" testid="entry-zoom" />
        </div>
      )}
      </div>
      )}
      </>)}

      {adding && (
        <MasterDialog kind={adding.kind} open={!!adding} presetName={adding.name} presetValues={adding.values} onOpenChange={(o) => !o && setAdding(null)}
          onSaved={(doc) => {
            if (adding.kind === "vendors") setF((p) => ({ ...p, vendor: doc, vendor_vat_number: doc.vat_number || p.vendor_vat_number }));
            else set("item", doc);
            setTimeout(() => focusTid(adding.kind === "vendors" ? "vendor-vat-no-input" : "bill-sponsor-input"), 120);
          }} />
      )}
      <Dialog open={confirmDup} onOpenChange={setConfirmDup}>
        <DialogContent className="bg-white" data-testid="duplicate-confirm-dialog">
          <DialogHeader>
            <DialogTitle>Save potential duplicate?</DialogTitle>
            <DialogDescription>A record with the same client, vendor and invoice number already exists. Saving will be logged.</DialogDescription>
          </DialogHeader>
          {dups.length > 0 && <DuplicateAlert dups={dups} />}
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDup(false)}>Review entry</Button>
            <Button className="bg-amber-600 hover:bg-amber-700" disabled={busy} onClick={() => submit(true)} data-testid="duplicate-confirm-save-button">Save anyway</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {saved && <BillDrawer billId={saved.id} open={drawer} onOpenChange={setDrawer} />}
    </div>
  );
}
